import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import styles from './EmailVerified.module.scss'
import { Icon } from '@/icons'
import { Button } from '@/components/Button'
import { apiService } from '@/services/Api'
import { getMailProviderLink } from '@/utils/emails'
import { formatTimerValue } from '@/utils/format'

interface EmailVerifiedProps {
  className?: string
  email: string
  isSuccessRegistration?: boolean
}
export const EmailVerified: FC<EmailVerifiedProps> = ({
  className,
  email,
  isSuccessRegistration = false,
}) => {
  const isInitRef = useRef(false)
  const [secondsLeft, setSecondsLeft] = useState(null)

  const resendEmail = useCallback(() => {
    return apiService.user
      .resendVerificationEmail({
        resendVerificationEmailRequest: {
          email,
        },
      })
      .then((response) => {
        if (response.status === true) {
          setSecondsLeft(response.data.nextAttemptIn)
        }
      })
  }, [email])

  // Первый запрос при монтировании; ref-гард защищает от повторного вызова
  // (в dev StrictMode эффект отрабатывает дважды)
  useEffect(() => {
    if (isInitRef.current) {
      return
    }

    isInitRef.current = true
    resendEmail()
  }, [resendEmail])

  const isTimerActive = secondsLeft > 0

  const mailProviderLink = useMemo(() => {
    return getMailProviderLink(email)
  }, [email])

  useEffect(() => {
    if (secondsLeft <= 0 || secondsLeft == null) {
      return
    }

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1)
    }, 1000)

    return () => clearTimeout(timer)
  }, [secondsLeft])

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.content}>
        <div className={styles.header}>
          {isSuccessRegistration ? (
            <Icon name="approved" />
          ) : (
            <div className={styles.iconTitleWrap}>
              <Icon name="attention" className={styles.iconTitle} />
            </div>
          )}
          <div className={styles.title}>
            {isSuccessRegistration
              ? 'Регистрация прошла успешно'
              : 'Необходимо подтвердить почту'}
          </div>
        </div>
        <div className={styles.description}>
          Мы отправили на вашу почту{' '}
          <span className={styles.emailText}>{email}</span> ссылку для
          подтверждения регистрации. После перехода по ссылке вы сможете начать
          работу с личным кабинетом.
        </div>
      </div>
      <div className={styles.buttons}>
        {mailProviderLink != null ? (
          <Button
            name="Перейти в почтовый ящик"
            href={mailProviderLink}
            target="_blank"
          />
        ) : null}

        {secondsLeft != null ? (
          <Button
            variant="secondary"
            name={
              isTimerActive
                ? `Отправить письмо повторно через ${formatTimerValue(
                    secondsLeft
                  )}`
                : 'Отправить письмо повторно'
            }
            onClick={resendEmail}
            disabled={isTimerActive}
          />
        ) : null}
      </div>
    </div>
  )
}
